import { useState, useCallback } from 'react';
import axios from 'axios';

export interface HourlyGeneration {
  timestamp: string;
  power_mw: number;
  energy_mwh: number;
  efficiency_factor: number;
  cloud_impact: number;
  weather_description: string;
}

export interface MonthlyEstimate {
  month: number;
  capacity_factor_percent: number;
  peak_sun_hours: number;
  monthly_energy_mwh: number;
  daily_average_mwh: number;
  best_day_mwh: number;
  worst_day_mwh: number;
  homes_powered_avg: number;
}

export interface SimulationResult {
  location: {
    lat: number;
    lon: number;
    name: string | null;
  };
  farm_area_acres: number;
  panel_efficiency: string;
  nameplate_capacity_mw: number;
  current_power_mw: number;
  current_efficiency: number;
  daily_energy_mwh: number;
  peak_power_mw: number;
  hourly_generation: HourlyGeneration[];
  homes_powered: number;
  homes_powered_assumption: string;
  good_day_energy_mwh: number;
  bad_day_energy_mwh: number;
  monthly_estimate: MonthlyEstimate | null;
}

export interface CalculatorResult {
  land_area_acres: number;
  panel_efficiency: string;
  efficiency_percentage: number;
  peak_power_mw: number;
  midday_peak_range_mw: [number, number];
  optimal_day_energy_mwh: number;
  average_day_energy_mwh: number;
  poor_day_energy_mwh: number;
  homes_powered_optimal: number;
  homes_powered_average: number;
  homes_powered_poor: number;
  assumptions: Record<string, string>;
}

export interface SimulationParams {
  lat: number;
  lon: number;
  farmAreaAcres: number;
  panelEfficiency: 'high' | 'average' | 'old';
  inverterLoss: number;
  soilingLoss: number;
  temperatureDerate: number;
}

interface UseSolarSimulationResult {
  simulation: SimulationResult | null;
  calculator: CalculatorResult | null;
  loading: boolean;
  error: string | null;
  runSimulation: (params: SimulationParams) => Promise<void>;
  runCalculator: (params: Pick<SimulationParams, 'lat' | 'lon' | 'farmAreaAcres' | 'panelEfficiency'>) => Promise<void>;
}

export function useSolarSimulation(): UseSolarSimulationResult {
  const [simulation, setSimulation] = useState<SimulationResult | null>(null);
  const [calculator, setCalculator] = useState<CalculatorResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runSimulation = useCallback(async (params: SimulationParams) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post<SimulationResult>('/api/simulate', {
        location: {
          lat: params.lat,
          lon: params.lon,
        },
        farm_area_acres: params.farmAreaAcres,
        panel_efficiency: params.panelEfficiency,
        inverter_loss: params.inverterLoss,
        soiling_loss: params.soilingLoss,
        temperature_derate: params.temperatureDerate,
      });
      setSimulation(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Simulation failed';
      setError(message);
      console.error('Simulation error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const runCalculator = useCallback(async (
    params: Pick<SimulationParams, 'lat' | 'lon' | 'farmAreaAcres' | 'panelEfficiency'>
  ) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post<CalculatorResult>('/api/calculate', {
        location: {
          lat: params.lat,
          lon: params.lon,
        },
        land_area_acres: params.farmAreaAcres,
        panel_efficiency: params.panelEfficiency,
      });
      setCalculator(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Calculation failed';
      setError(message);
      console.error('Calculator error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    simulation,
    calculator,
    loading,
    error,
    runSimulation,
    runCalculator,
  };
}

